import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as IconPrescription } from 'bootstrap-icons/icons/prescription.svg'
import { ReactComponent as IconEye } from 'bootstrap-icons/icons/eye.svg'
import useMutation from 'hooks/useMutation';

import { useQuery } from "react-query";
import { formatDate } from 'config/i18n'
import EHRVisibilityModal from '../EHRVisibilityModal'
import sanitize from '../sanitize'

let MAX_TIME = 48 * 60 * 60 * 1000

let {
  REACT_APP_API_URL,
  REACT_APP_PRESCRIPTION_FRONT_URL
} = process.env

let clone = window.structuredClone || JSON.clone

let get = path => () => fetch(`${REACT_APP_API_URL}/${path}`).then(res => res.json())

function Prescription({ prescription, user, canEhrConfig, onEditVisibility, resourceEhrConfig, resource }) {
  const [showModal, setShowModal] = useState(false)
  const [prescriptionData, setPrescriptionData] = useState(null)
  const [mutateVisibility, mutationVisibility] = useMutation(`prescriptions/${prescription.id}/${prescription.token}/visibility`);

  let { t } = useTranslation();

  const handleEditVisibility = () => {
    // Si no hay visibility configurada, usar la configuración del recurso o público por defecto
    if (!prescriptionData?.payload?.visibility) {
      setPrescriptionData(prev => ({
        ...prev,
        payload: { ...prev.payload, visibility: { type: 'default' } }
      }));
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleSaveVisibility = async () => {
    // Aquí puedes implementar la lógica para guardar la visibilidad
    mutateVisibility(prescriptionData.payload?.visibility, {
      onSuccess: () => {
        setShowModal(false);
      }
    })
  }

  let query = useQuery(
    `prescriptions/${prescription.id}/${prescription.token}`,
    get(`prescriptions/${prescription.id}/${prescription.token}`),
    {
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        setPrescriptionData(clone(data))
      }
    }
  )


  if (!prescriptionData?.payload || !prescriptionData.payload.resource?.label) return null

  let { payload, integrations, pdf } = prescriptionData
  let treatments = Array.isArray(payload.treatments)
    ? payload.treatments
    : (payload.treatments && typeof payload.treatments === 'object' ? Object.values(payload.treatments) : [])
  let diagnostic = payload.diagnostic
  let indications = payload.indications
  let content = payload.content

  const prescriptionResourceID = payload.resourceID || payload.resource?.id

  return (
    <div className="bg-white shadow-sm mb-3 p-3">
      <div className="d-flex flex-column gap-2">
        <div className="d-flex align-items-center gap-2 mb-2 text-xs">
          <div className="text-secondary">
            <IconPrescription /> {formatDate(payload.date, "dd-MMM-yy").toUpperCase()}
          </div>
          <div className="text-secondary">{payload.resource.label}</div>
          <div className="flex-fill text-end d-flex align-items-center gap-2">
            {payload.createdBy === user.state.uid && payload.createdAt + MAX_TIME >= Date.now() && canEhrConfig && prescriptionResourceID === resource.id && (
              <button
                onClick={handleEditVisibility}
                className="btn btn-sm btn-link p-0 d-flex align-items-center"
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t('ehr:edit_visibility', 'Editar visibilidad')}
                data-tooltip-place="right"
              >
                <IconEye width="16" height="16" />
              </button>
            )}
            <a
              href={pdf}
              target="_blank"
              rel="noopener noreferrer"
              className="ms-3"
            >{t('ehr:open_prescription_pdf', 'PDF')}</a>
            {REACT_APP_PRESCRIPTION_FRONT_URL && (
              <a
                href={`${REACT_APP_PRESCRIPTION_FRONT_URL}/${prescription.id}/${prescription.token}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ms-3"
              >{t('ehr:open_prescription_link', 'Link')}</a>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex flex-column gap-3">
        {diagnostic && (
          <div>
            <h6 className="font-small-caps text-secondary mb-0">{t('ehr:prescription_diagnostic', 'Diagnostico')}</h6>
            <div className="lh-1">
              {/* Bug real (auditoría 2026-08-16): dangerouslySetInnerHTML sobre texto libre
                  (diagnóstico cargado por el profesional) sin sanitizar — XSS persistente. */}
              <small dangerouslySetInnerHTML={{ __html: sanitize(diagnostic) }} />
            </div>
          </div>
        )}
        {indications && (
          <div>
            <h6 className="font-small-caps text-secondary mb-0">{t('ehr:prescription_indications', 'Indicaciones')}</h6>
            <div className="lh-1">
              {/* Bug real (auditoría 2026-08-16): dangerouslySetInnerHTML sobre texto libre
                  (indicaciones cargadas por el profesional) sin sanitizar — XSS persistente. */}
              <small dangerouslySetInnerHTML={{ __html: sanitize(indications) }} />
            </div>
          </div>
        )}
        {content && (
          <div>
            <h6 className="font-small-caps text-secondary mb-0">{t('ehr:prescription_order_study', 'Orden')}</h6>
            <div className="lh-1">
              {/* Bug real (auditoría 2026-08-16): dangerouslySetInnerHTML sobre texto libre
                  (orden/estudio cargado por el profesional) sin sanitizar — XSS persistente. */}
              <small dangerouslySetInnerHTML={{ __html: sanitize(content) }} />
            </div>
          </div>
        )}
        {treatments.length > 0 && (
          <div>
            <h6 className="font-small-caps text-secondary mb-0">{t('ehr:prescription_treatments', 'Tratamientos')}</h6>
            <ul className="mb-0">
              {treatments.map(item => (
                // Bug real (auditoría 2026-08-16): dangerouslySetInnerHTML sobre texto libre
                // (nombre de tratamiento/droga) sin sanitizar — XSS persistente.
                <li key={item.product_code} dangerouslySetInnerHTML={{ __html: sanitize(item.label || item.product) }} />
              ))}
            </ul>
          </div>
        )}
      </div>

      {showModal && (
        <EHRVisibilityModal
          onHide={handleCloseModal}
          visibility={prescriptionData?.payload?.visibility || { type: 'default' }}
          onChange={(visibility) => {
            setPrescriptionData(prev => ({
              ...prev,
              payload: { ...prev.payload, visibility }
            }))
          }}
          onSave={handleSaveVisibility}
          type="record"
          resourceVisibility={resourceEhrConfig?.visibility || { type: 'public' }}
          resource={resource}
        />
      )}
    </div>
  )
}

export function Prescriptions({ prescriptions, user, onEditVisibility, resourceEhrConfig, resource }) {
  let { t } = useTranslation();

  if (!Array.isArray(prescriptions) || prescriptions.length === 0) return null

  const canEhrConfig = (user.team?.permissions && user.team.permissions.teamConfig !== false)
    || (user.team?.permissions && user.team.permissions.ehrConfig !== false && user.team?.resourceID === resource.id);


  return (
    <div>
      <h6 className="font-small-caps text-secondary mb-0">{t('ehr:prescriptions', 'Recetas')}</h6>
      {prescriptions.map(prescription => (
        <Prescription
          prescription={prescription}
          user={user}
          onEditVisibility={onEditVisibility}
          canEhrConfig={canEhrConfig}
          resourceEhrConfig={resourceEhrConfig}
          key={`${prescription.id}_${prescription.token}`}
          resource={resource}
        />
      ))}
    </div>
  );
}
